import { useNavigate } from "@tanstack/react-router";
import { X as CloseIcon, Menu02 } from "@untitledui/icons";
import type { PropsWithChildren } from "react";
import { createContext, useContext } from "react";
import {
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Modal as AriaModal,
	ModalOverlay as AriaModalOverlay,
} from "react-aria-components";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { useTheme } from "@/contexts/ThemeProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";
import { ViewTransitionTypes, viewTransition } from "@/utils/view-transitions";
import { AvatarDropdown } from "../AvatarDropdown/AvatarDropdown";

// Create context for mobile navigation close function
const MobileNavCloseContext = createContext<(() => void) | null>(null);

// Hook to use the mobile nav close function
export const useMobileNavClose = () => {
	return useContext(MobileNavCloseContext);
};

export const MobileLeftNav = ({
	children,
	homeUrl,
	name,
	email,
	onSignOut,
	onUserProfile = () => {},
	variant = "light",
	logoSrc = "/images/AdminLogo.png",
	logoClassName,
}: PropsWithChildren<{
	homeUrl?: string;
	name: string;
	email: string;
	onSignOut: () => void;
	onUserProfile?: () => void;
	variant?: "light" | "brand";
	logoSrc?: string;
	/** Optional class for the brand logo image. */
	logoClassName?: string;
}>) => {
	const isBrand = variant === "brand";
	const navigate = useNavigate();
	const theme = useTheme();
	const { t } = useAppTranslation();
	const brandLogoClassName =
		logoClassName ?? "h-10 w-[163px] object-contain object-left shrink-0";

	return (
		<AriaDialogTrigger>
			<header
				className={cx(
					"w-full flex h-16 items-center justify-between border-b py-3 pr-2 pl-4 lg:hidden",
					isBrand
						? "border-[rgba(255,255,255,0.12)]"
						: "border-secondary bg-primary",
				)}
				style={
					isBrand
						? {
								background: "var(--gradient-brand-primary02)",
							}
						: undefined
				}
			>
				<div className="flex items-center gap-3">
					<AriaButton
						aria-label={t("main.nav.menu.expand")}
						className={cx(
							"group flex items-center justify-center rounded-lg p-2 outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2",
							isBrand
								? "text-alpha-white-60 hover:bg-[rgba(255,255,255,0.08)] hover:text-white bg-transparent"
								: "bg-primary text-fg-secondary hover:bg-primary_hover hover:text-fg-secondary_hover",
						)}
					>
						<Menu02 className="size-6 transition duration-200 ease-in-out group-aria-expanded:opacity-0" />
						<CloseIcon className="absolute size-6 opacity-0 transition duration-200 ease-in-out group-aria-expanded:opacity-100" />
					</AriaButton>
					<button
						type="button"
						aria-label={t("main.nav.goToHomepage")}
						onClick={() =>
							navigate({
								to: homeUrl || "/web/$orgId/home",
								...viewTransition(ViewTransitionTypes.FADE),
							})
						}
						className="rounded-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 cursor-pointer bg-transparent border-0 p-0"
					>
						<img
							src={
								isBrand
									? logoSrc
									: theme.appTheme === "light"
										? "https://opscdn.blob.core.windows.net/assets/lightBackground/Horizontal Logo/OpSource-Logo-LB-Horizontal-FullColor.png"
										: "https://opscdn.blob.core.windows.net/assets/darkBackground/Horizontal Logo/OpSource-Logo-DB-Horizontal-FullColor.png"
							}
							alt={t("common.logoAlt")}
							className={cx(
								"object-contain",
								isBrand ? brandLogoClassName : "h-10 w-[139px]",
							)}
						/>
					</button>
				</div>
				{!isBrand && (
					<div className="flex items-center gap-3">
						<Tooltip
							title={t("main.nav.menu.account.accountMenu")}
							placement="bottom"
							delay={500}
						>
							<AvatarDropdown
								name={name}
								email={email}
								onSignOut={onSignOut}
								onUserProfile={onUserProfile}
							/>
						</Tooltip>
					</div>
				)}
			</header>

			<AriaModalOverlay
				isDismissable
				className={({ isEntering, isExiting }) =>
					cx(
						"fixed inset-0 z-50 cursor-pointer bg-overlay/70 pr-16 backdrop-blur-md lg:hidden",
						isEntering && "duration-300 ease-in-out animate-in fade-in",
						isExiting && "duration-200 ease-in-out animate-out fade-out",
					)
				}
			>
				{({ state }) => (
					<>
						<AriaButton
							aria-label={t("main.nav.menu.collapse")}
							onPress={() => state.close()}
							className="fixed top-3 right-2 flex cursor-pointer items-center justify-center rounded-lg p-2 text-fg-white/70 outline-focus-ring hover:bg-white/10 hover:text-fg-white focus-visible:outline-2 focus-visible:outline-offset-2"
						>
							<CloseIcon className="size-6" />
						</AriaButton>

						<AriaModal className="w-full cursor-auto will-change-transform">
							<AriaDialog className="h-dvh outline-hidden focus:outline-hidden">
								<aside
									className="flex h-full max-h-full w-full max-w-full flex-col justify-between overflow-y-auto pt-3"
									style={
										isBrand
											? {
													background: "var(--gradient-brand-primary02)",
												}
											: { backgroundColor: "var(--color-bg-primary)" }
									}
								>
									<div className="px-4">
										<button
											type="button"
											aria-label={t("main.nav.goToHomepage")}
											onClick={() => {
												navigate({
													to: homeUrl || "/web/$orgId/home",
													...viewTransition(ViewTransitionTypes.FADE),
												});
												state.close();
											}}
											className="rounded-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 cursor-pointer bg-transparent border-0 p-0"
										>
											<img
												src={
													isBrand
														? logoSrc
														: theme.appTheme === "light"
															? "https://opscdn.blob.core.windows.net/assets/lightBackground/Horizontal Logo/OpSource-Logo-LB-Horizontal-FullColor.png"
															: "https://opscdn.blob.core.windows.net/assets/darkBackground/Horizontal Logo/OpSource-Logo-DB-Horizontal-FullColor.png"
												}
												alt={t("common.logoAlt")}
												className={cx(
													"object-contain",
													isBrand ? brandLogoClassName : "h-10 w-[139px]",
												)}
											/>
										</button>
									</div>
									{/* Provide the close function via context */}
									<MobileNavCloseContext.Provider value={state.close}>
										{children}
									</MobileNavCloseContext.Provider>
								</aside>
							</AriaDialog>
						</AriaModal>
					</>
				)}
			</AriaModalOverlay>
		</AriaDialogTrigger>
	);
};
